import { Accordion, AccordionDetails, AccordionSummary, createStyles, makeStyles, Typography } from '@material-ui/core';
import cn from 'classnames';
import React from 'react';
import { TranquiTheme } from '../containers/App/theme';
import MarkdownRender from '../dictionaries/MarkdownRender';
import { NegotiationOption, NegotiationType } from '../generated/graphql';
import { bogotaTotalPaymentSteps } from './bogotaTotalPaymentSteps';
import { getImageUrl } from './imagesRoot';
import { getPaymentGuideLabel } from './PaymentGuideType';
import { bogotaBalanceBehindPaymentSteps } from './bogotaBalanceBehindPaymentSteps';
import { bogotaRefinanceSteps } from './bogotaRefinanceSteps';
import { bogotaPaymentPromiseSteps } from './bogotaPaymentPromiseSteps';

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  root: {
    padding: theme.spacing(3, 0),
  },
  accordion: {
    boxShadow: theme.shadows[5],
    borderBottom: `solid 1px ${theme.palette.indigo.main}40`,
    borderRadius: theme.spacing(0.5),
    overflow: 'hidden',
  },
  accordionSelected: {
    border: `solid 1px ${theme.palette.indigo.main}40`,
    '& $accordionTitleCard': {
      background: theme.palette.indigo.main,
      '& $accordionTitle': {
        color: theme.palette.pearl.main,
      },
    },
  },
  accordionTitleCard: {
    transition: 'all .4s',
  },
  accordionTitle: {
    ...theme.typography.body1,
    fontWeight: 600,
    color: theme.palette.indigo.main,
    transition: 'all .4s',
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    backgroundColor: theme.palette.marble.main,
    padding: theme.spacing(2),
    '& typography': {
      margin: 0,
    },
  },
  ol: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
    overflowWrap: 'anywhere',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    listStylePosition: 'inside',
    '& li::marker': {
      fontWeight: 'bold',
    },
    '& li': {
      padding: 0,
      margin: 0,
    },
  },
  generalTextTitle: {
    ...theme.typography.body1,
    lineHeight: 'inherit',
  },
  dividerTitle: {
    marginTop: theme.spacing(2),
  },
  generalText: {
    ...theme.typography.subtitle1,
    lineHeight: 'inherit',
  },
  littleText: {
    ...theme.typography.subtitle2,
    lineHeight: 'inherit',
  },
  tinyText: {
    ...theme.typography.subtitle2,
    color: theme.palette.grey[500],
    '& a': {
      color: theme.palette.lavender.main,
      fontWeight: 600,
    },
  },
}));

type BogotaPaymentGuidesProps = { negOption: NegotiationOption };

export const BogotaPaymentGuides: React.FC<BogotaPaymentGuidesProps> = (props) => {
  const { negOption } = props;
  const {
    accordionTitle, accordion, accordionSelected, accordionTitleCard, details, ol,
  } = useStyles();
  const [ expanded, setExpanded ] = React.useState<string | false>(false);

  const handleChangeAccordion = (panel: string) => (_event: any, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const steppersMap: { [key: string]: typeof bogotaTotalPaymentSteps | undefined } = {
    [NegotiationType.TotalPayment]: bogotaTotalPaymentSteps,
    [NegotiationType.BalanceBehindPayment]: bogotaBalanceBehindPaymentSteps,
    [NegotiationType.Refinance]: bogotaRefinanceSteps,
    [NegotiationType.PaymentPromise]: bogotaPaymentPromiseSteps,
  };

  const steppers = steppersMap[`${negOption.type}`];
  const noSteppers = <Typography className={accordionTitle}>No hay medios de pago.</Typography>;
  if (!steppers?.length) return noSteppers;

  const paymentGuide = steppers.map(({ type, steps }) => {
    return (
      <Accordion
        className={cn(accordion, expanded === `${type}` && accordionSelected)}
        onChange={handleChangeAccordion(`${type}`)}
        expanded={expanded === `${type}` || steppers.length === 1}
        key={type}
      >
        <AccordionSummary className={accordionTitleCard}>
          <Typography className={accordionTitle}>
            Cómo pagar en {getPaymentGuideLabel(type)}
          </Typography>
        </AccordionSummary>
        <AccordionDetails className={details}>
          <ol className={ol}>
            {steps.map(({ text, image, indents }) => {
              const img = !!image && getImageUrl(image);
              return (
                <li
                  style={{
                    paddingLeft: (indents || 0) * 60,
                    listStyleType: 'none',
                  }}
                >
                  <MarkdownRender
                    text={text}
                  />
                  {!!img && (<img src={img} alt={text} width="100%" />)}
                </li>
              );
            })}
          </ol>
        </AccordionDetails>
      </Accordion>
    );
  });

  return <>{paymentGuide}</>;
};
